import { Alert, Spin } from "antd";
import type { Data } from "plotly.js";
import Plot, { Figure } from "react-plotly.js";
import type { ImbalancePriceInsight } from "../../../services/client";
import { ONE_MINUTE } from "../../../util/time";
import NoData from "../../NewSite/Components/OptimizationConfigurationForm/NoData";
import { plotlyDateAxis } from "../../../util/formatDate";
import {
  ACTUAL_COLOR,
  GRID_COLOR,
  LINE_HALO_WIDTH,
  NOW_COLOR,
  PREDICTION_COLOR,
  SURFACE_COLOR,
  quarterHourStarts,
  stepLine,
} from "./imbalanceChartLines";

const MINUTE_FORECAST_COLOR = "#722ed1";
// Percentile bands of the per-minute forecast: the outer 10-90 band is drawn
// first and lighter, the inner 25-75 band on top of it.
const MINUTE_BANDS = [
  {
    low: "0.1",
    high: "0.9",
    name: "Per-minute 10-90 percentile",
    color: "#efdbff",
  },
  {
    low: "0.25",
    high: "0.75",
    name: "Per-minute 25-75 percentile",
    color: "#d3adf7",
  },
] as const;

// Widest band first, so the lighter bands of predictions made closer to the
// quarter hour are drawn on top of it and earlier predictions that disagreed
// show as a darker edge.
const BANDS = [
  { hoursBefore: 24, color: "#aeaeae" },
  { hoursBefore: 12, color: "#c1c1c1" },
  { hoursBefore: 4, color: "#d4d4d4" },
  { hoursBefore: 1, color: "#e8e8e8" },
] as const;

type ImbalanceForecastChartProps = {
  insights: ImbalancePriceInsight[];
  isLoading: boolean;
  error: Error | null;
};

// Prices hold for the whole quarter hour, so the last step needs a closing
// point or it renders as a bare dot.
const closeLastQuarterHour = <T,>(values: T[]): T[] =>
  values.length === 0 ? values : [...values, values[values.length - 1]];

// Each quarter hour gets its own step line: the forecast made for every minute,
// held until the next minute. A gap separates quarter hours so a line never
// suggests a forecast for one quarter hour carried into the next.
const minuteForecastTraces = (insights: ImbalancePriceInsight[]): Data[] => {
  const name = "Per-minute forecast (active quarter hour)";
  const x: (number | null)[] = [];
  const y: (number | null)[] = [];
  const minutes: (number | null)[] = [];
  insights.forEach(({ minute_forecasts }) => {
    if (!minute_forecasts || minute_forecasts.length === 0) {
      return;
    }
    minute_forecasts.forEach((forecast) => {
      x.push(new Date(forecast.valid_from).getTime());
      y.push(forecast.predicted_price);
      minutes.push(forecast.minute);
    });
    const last = minute_forecasts[minute_forecasts.length - 1];
    x.push(new Date(last.valid_from).getTime() + ONE_MINUTE, null);
    y.push(last.predicted_price, null);
    minutes.push(last.minute, null);
  });
  if (x.length === 0) {
    return [];
  }
  return [
    {
      x,
      y,
      type: "scatter",
      mode: "lines",
      connectgaps: false,
      line: { shape: "hv", color: SURFACE_COLOR, width: LINE_HALO_WIDTH },
      legendgroup: name,
      showlegend: false,
      hoverinfo: "skip",
    },
    {
      x,
      y,
      customdata: minutes,
      type: "scatter",
      mode: "lines",
      connectgaps: false,
      line: { shape: "hv", color: MINUTE_FORECAST_COLOR, width: 1.5 },
      legendgroup: name,
      name,
      hovertemplate: `%{y:.2f} (minute %{customdata})<extra>${name}</extra>`,
    },
  ];
};

// Per-minute percentile band, one filled area per quarter hour with a gap
// between quarter hours like the per-minute line. Minutes without quantiles
// (forecasts made before the quantile model was deployed) leave a gap.
const minuteBandTraces = (
  insights: ImbalancePriceInsight[],
  low: string,
  high: string,
  name: string,
  color: string,
): Data[] => {
  const x: (number | null)[] = [];
  const lower: (number | null)[] = [];
  const upper: (number | null)[] = [];
  insights.forEach(({ minute_forecasts }) => {
    if (!minute_forecasts || minute_forecasts.length === 0) {
      return;
    }
    let drawn = false;
    minute_forecasts.forEach((forecast) => {
      const quantiles = forecast.predicted_price_quantiles;
      const lowValue = quantiles?.[low];
      const highValue = quantiles?.[high];
      if (lowValue === undefined || highValue === undefined) {
        return;
      }
      x.push(new Date(forecast.valid_from).getTime());
      lower.push(lowValue);
      upper.push(highValue);
      drawn = true;
    });
    if (!drawn) {
      return;
    }
    const lastIndex = x.length - 1;
    x.push((x[lastIndex] as number) + ONE_MINUTE, null);
    lower.push(lower[lastIndex], null);
    upper.push(upper[lastIndex], null);
  });
  if (x.length === 0) {
    return [];
  }
  return [
    {
      x,
      y: lower,
      type: "scatter",
      mode: "lines",
      connectgaps: false,
      line: { shape: "hv", width: 0 },
      legendgroup: name,
      showlegend: false,
      hoverinfo: "skip",
    },
    {
      x,
      y: upper,
      customdata: lower,
      type: "scatter",
      mode: "lines",
      connectgaps: false,
      fill: "tonexty",
      fillcolor: color,
      line: { shape: "hv", width: 0 },
      legendgroup: name,
      name,
      hovertemplate: `%{customdata:.2f} – %{y:.2f}<extra>${name}</extra>`,
    },
  ];
};

const bandTraces = (
  x: number[],
  insights: ImbalancePriceInsight[],
  hoursBefore: number,
  color: string,
): Data[] => {
  const bands = insights.map((insight) =>
    insight.bands.find((band) => band.hours_before === hoursBefore),
  );
  const lower = closeLastQuarterHour(bands.map((band) => band?.lower ?? null));
  const upper = closeLastQuarterHour(bands.map((band) => band?.upper ?? null));
  const name = `Predicted within ${hoursBefore}h`;
  return [
    {
      x,
      y: lower,
      type: "scatter",
      mode: "lines",
      line: { shape: "hv", width: 0 },
      legendgroup: name,
      showlegend: false,
      hoverinfo: "skip",
    },
    {
      x,
      y: upper,
      customdata: lower,
      type: "scatter",
      mode: "lines",
      fill: "tonexty",
      fillcolor: color,
      line: { shape: "hv", width: 0 },
      legendgroup: name,
      name,
      hovertemplate: `%{customdata:.2f} – %{y:.2f}<extra>${name}</extra>`,
    },
  ];
};

export const ImbalanceForecastChart: React.FC<ImbalanceForecastChartProps> = ({
  insights,
  isLoading,
  error,
}) => {
  if (error) {
    return <Alert message="Error" description={error.message} type="error" />;
  }

  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center min-h-96">
        <Spin />
      </div>
    );
  }

  if (insights.length === 0) {
    return (
      <NoData message="No imbalance prices or forecasts available for this range." />
    );
  }

  const x = quarterHourStarts(insights);
  const now = Date.now();
  const data: Data[] = [
    ...BANDS.flatMap(({ hoursBefore, color }) =>
      bandTraces(x, insights, hoursBefore, color),
    ),
    ...stepLine(
      x,
      insights.map((insight) => insight.latest_prediction),
      "Latest forecast",
      PREDICTION_COLOR,
    ),
    ...stepLine(
      x,
      insights.map((insight) => insight.actual_price),
      "Actual",
      ACTUAL_COLOR,
    ),
    ...MINUTE_BANDS.flatMap(({ low, high, name, color }) =>
      minuteBandTraces(insights, low, high, name, color),
    ),
    // Last, so the per-minute steps stay visible once the actual price lands
    // within a few €/MWh of them.
    ...minuteForecastTraces(insights),
  ];

  const layout: Figure["layout"] = {
    title: "BE imbalance price",
    plot_bgcolor: SURFACE_COLOR,
    paper_bgcolor: SURFACE_COLOR,
    xaxis: { ...plotlyDateAxis, title: "Time", gridcolor: GRID_COLOR },
    yaxis: {
      title: "€/MWh",
      gridcolor: GRID_COLOR,
      zeroline: true,
      zerolinecolor: "#d9d9d9",
    },
    shapes: [
      {
        type: "line",
        x0: now,
        x1: now,
        yref: "paper",
        y0: 0,
        y1: 1,
        line: { color: NOW_COLOR, width: 1, dash: "dot" },
      },
    ],
    annotations: [
      {
        x: now,
        yref: "paper",
        y: 1,
        text: "now",
        showarrow: false,
        yanchor: "bottom",
        font: { color: NOW_COLOR },
      },
    ],
    legend: { orientation: "h", traceorder: "reversed" },
    hovermode: "x unified",
    margin: { t: 60, r: 20, b: 50, l: 70 },
  };

  return (
    <Plot
      data={data}
      layout={layout}
      useResizeHandler
      style={{ width: "100%", height: 520 }}
      config={{ displaylogo: false, responsive: true }}
    />
  );
};
